import React, { useState, useEffect } from "react";
import {
  Paper, Grid, Typography, TextField, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress,
} from "@mui/material";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { format, subDays } from "date-fns";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const STATUS_LABELS = { open: "Aberto", pending: "Pendente", closed: "Resolvido" };
const STATUS_COLORS = { open: "#F37021", pending: "#F59E0B", closed: "#10B981" };

const Reports = () => {
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/tickets/reports", { params: { startDate, endDate } });
      setData(d);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const dayData = data ? data.byDay.map(d => ({ dia: d.day, Novos: +d.opened, Resolvidos: +d.closed })) : [];
  const queueData = data ? data.byQueue.map(d => ({ fila: d.queueName || "Sem fila", Tickets: +d.count })) : [];

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatórios</Title>
        <MainHeaderButtonsWrapper>
          <TextField type="date" size="small" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} label="De" />
          <TextField type="date" size="small" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} label="Até" style={{ marginLeft: 8 }} />
          <Button variant="contained" color="primary" onClick={load} disabled={loading} style={{ marginLeft: 8 }}>
            {loading ? <CircularProgress size={18} /> : "Aplicar"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {!data ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><CircularProgress /></div>
      ) : (
        <Grid container spacing={2}>
          {/* Cards de status */}
          {data.byStatus.map(s => (
            <Grid item xs={6} sm={3} key={s.status}>
              <Paper variant="outlined" style={{ padding: 16, textAlign: "center" }}>
                <Typography variant="h4" style={{ color: STATUS_COLORS[s.status] || "#666", fontWeight: 700 }}>{s.count}</Typography>
                <Typography variant="body2" color="textSecondary">{STATUS_LABELS[s.status] || s.status}</Typography>
              </Paper>
            </Grid>
          ))}

          {/* Gráfico diário */}
          <Grid item xs={12}>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Tickets por dia</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dayData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip />
                  <Legend />
                  <Bar dataKey="Novos" fill="#F37021" />
                  <Bar dataKey="Resolvidos" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Gráfico por fila */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Por fila</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={queueData} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="fila" tick={{ fontSize: 11 }} />
                  <RTooltip />
                  <Bar dataKey="Tickets" fill="#2576D2" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Tabela por atendente */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Por atendente</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Atendente</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Resolvidos</TableCell>
                    <TableCell align="right">Tempo médio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.byAgent.map((a, i) => {
                    const avg = a.avgMinutes != null ? Number(a.avgMinutes) : null;
                    const avgLabel = avg == null ? "—" : avg >= 60 ? `${Math.floor(avg/60)}h ${avg%60}m` : `${avg}m`;
                    return (
                    <TableRow key={i}>
                      <TableCell>{a.agentName || "—"}</TableCell>
                      <TableCell align="right">{a.count}</TableCell>
                      <TableCell align="right">{a.resolved}</TableCell>
                      <TableCell align="right">{avgLabel}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}
    </MainContainer>
  );
};

export default Reports;
